"use client";

import { useEffect, useRef, useState } from "react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { useTranslation } from "@/hooks/useTranslation";
import { useRTL } from "@/hooks/useRTL";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Sparkles,
  Clock,
  Zap,
  Home,
  Building2,
  Star,
  CheckCircle,
  X,
  ArrowRight,
  Shield,
} from "lucide-react";

gsap.registerPlugin(ScrollTrigger);

export default function WhiteningTypesSection() {
  const { t, isLoading } = useTranslation("teeth-whitening");
  const { isRTL } = useRTL();
  const [activeTab, setActiveTab] = useState("in-office");
  const sectionRef = useRef<HTMLElement>(null);
  const titleRef = useRef<HTMLDivElement>(null);
  const tabsRef = useRef<HTMLDivElement>(null);
  const tabsListRef = useRef<HTMLDivElement>(null);

  // When the active tab changes on mobile, scroll it into the visible area of
  // the horizontal tab strip so users can read its full label. Deferred with
  // requestAnimationFrame so Radix has finished flipping data-state before we
  // query for the active element.
  useEffect(() => {
    const rafId = requestAnimationFrame(() => {
      const list = tabsListRef.current;
      if (!list) return;
      const active = list.querySelector<HTMLElement>('[data-state="active"]');
      if (!active) return;
      const listRect = list.getBoundingClientRect();
      const activeRect = active.getBoundingClientRect();
      if (activeRect.left < listRect.left || activeRect.right > listRect.right) {
        active.scrollIntoView({ behavior: "smooth", block: "nearest", inline: "center" });
      }
    });
    return () => cancelAnimationFrame(rafId);
  }, [activeTab]);

  useEffect(() => {
    if (isLoading) return;

    const ctx = gsap.context(() => {
      // Title animation
      if (titleRef.current) {
        gsap.fromTo(
          titleRef.current,
          { opacity: 0, y: 50 },
          {
            opacity: 1,
            y: 0,
            duration: 1,
            ease: "power2.out",
            scrollTrigger: {
              trigger: titleRef.current,
              start: "top 85%",
              toggleActions: "play none none reverse",
            },
          }
        );
      }

      // Tabs animation
      if (tabsRef.current) {
        gsap.fromTo(
          tabsRef.current,
          { opacity: 0, y: 30 },
          {
            opacity: 1,
            y: 0,
            duration: 0.8,
            ease: "power2.out",
            scrollTrigger: {
              trigger: tabsRef.current,
              start: "top 85%",
              toggleActions: "play none none reverse",
            },
          }
        );
      }
    }, sectionRef);

    return () => ctx.revert();
  }, [isLoading]);

  const whiteningTypes = {
    "in-office": {
      icon: Building2,
      title: t("whiteningTypes.inOffice.title"),
      shortTitle: t("whiteningTypes.inOffice.shortTitle"),
      subtitle: t("whiteningTypes.inOffice.subtitle"),
      duration: t("whiteningTypes.inOffice.duration"),
      results: t("whiteningTypes.inOffice.results"),
      price: t("whiteningTypes.inOffice.price"),
      description: t("whiteningTypes.inOffice.description"),
      advantages: [
        t("whiteningTypes.inOffice.advantages.0"),
        t("whiteningTypes.inOffice.advantages.1"),
        t("whiteningTypes.inOffice.advantages.2"),
        t("whiteningTypes.inOffice.advantages.3"),
      ],
      disadvantages: [
        t("whiteningTypes.inOffice.disadvantages.0"),
        t("whiteningTypes.inOffice.disadvantages.1"),
      ],
      recommended: true,
    },
    "at-home": {
      icon: Home,
      title: t("whiteningTypes.atHome.title"),
      shortTitle: t("whiteningTypes.atHome.shortTitle"),
      subtitle: t("whiteningTypes.atHome.subtitle"),
      duration: t("whiteningTypes.atHome.duration"),
      results: t("whiteningTypes.atHome.results"),
      price: t("whiteningTypes.atHome.price"),
      description: t("whiteningTypes.atHome.description"),
      advantages: [
        t("whiteningTypes.atHome.advantages.0"),
        t("whiteningTypes.atHome.advantages.1"),
        t("whiteningTypes.atHome.advantages.2"),
        t("whiteningTypes.atHome.advantages.3"),
      ],
      disadvantages: [
        t("whiteningTypes.atHome.disadvantages.0"),
        t("whiteningTypes.atHome.disadvantages.1"),
        t("whiteningTypes.atHome.disadvantages.2"),
      ],
      recommended: false,
    },
    laser: {
      icon: Zap,
      title: t("whiteningTypes.laser.title"),
      shortTitle: t("whiteningTypes.laser.shortTitle"),
      subtitle: t("whiteningTypes.laser.subtitle"),
      duration: t("whiteningTypes.laser.duration"),
      results: t("whiteningTypes.laser.results"),
      price: t("whiteningTypes.laser.price"),
      description: t("whiteningTypes.laser.description"),
      advantages: [
        t("whiteningTypes.laser.advantages.0"),
        t("whiteningTypes.laser.advantages.1"),
        t("whiteningTypes.laser.advantages.2"),
        t("whiteningTypes.laser.advantages.3"),
      ],
      disadvantages: [
        t("whiteningTypes.laser.disadvantages.0"),
        t("whiteningTypes.laser.disadvantages.1"),
      ],
      recommended: false,
    },
  };

  if (isLoading) {
    return (
      <section className="py-16 lg:py-24 bg-slate-50 dark:bg-slate-900">
        <div className="container">
          <div className="text-center mb-16 animate-pulse">
            <div className="h-12 bg-gray-200 dark:bg-gray-700 rounded mb-6 max-w-4xl mx-auto"></div>
            <div className="h-6 bg-gray-200 dark:bg-gray-700 rounded max-w-3xl mx-auto"></div>
          </div>
          <div className="max-w-6xl mx-auto">
            <div className="h-64 bg-gray-200 dark:bg-gray-700 rounded-xl animate-pulse"></div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section
      id="whitening-types"
      ref={sectionRef}
      className="py-16 lg:py-24 bg-slate-50 dark:bg-slate-900"
    >
      <div className="container">
        <div ref={titleRef} className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-navy dark:text-white font-serif leading-tight mb-6">
            {t("whiteningTypes.title.part1")}{" "}
            <span className="text-gold">
              {t("whiteningTypes.title.part2")}
            </span>
          </h2>
          <p className="text-lg text-slate-600 dark:text-slate-300 max-w-3xl mx-auto">
            {t("whiteningTypes.subtitle")}
          </p>
        </div>

        <div ref={tabsRef} className="max-w-6xl mx-auto">
          <Tabs
            value={activeTab}
            onValueChange={setActiveTab}
            className="w-full"
          >
            <TabsList
              ref={tabsListRef}
              className="!h-auto grid grid-cols-3 w-full mb-6 md:mb-10 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 p-2 md:p-4 rounded-xl shadow-sm gap-1 sm:gap-2 md:gap-0"
            >
              {Object.entries(whiteningTypes).map(([key, type]) => (
                <TabsTrigger
                  key={key}
                  value={key}
                  className="flex flex-col md:flex-row items-center justify-center gap-1 md:gap-3 py-2 md:py-5 px-2 md:px-4 rounded-lg data-[state=active]:bg-gold data-[state=active]:text-navy data-[state=active]:shadow-md transition-all duration-300 text-xs md:text-base font-medium hover:bg-gray-50 dark:hover:bg-slate-700 min-h-[56px] md:min-h-[64px] text-center leading-tight"
                >
                  <type.icon className="w-4 h-4 md:w-5 md:h-5 flex-shrink-0" />
                  <span className="font-medium md:whitespace-nowrap">
                    <span className="md:hidden">{type.shortTitle || type.title}</span>
                    <span className="hidden md:inline">{type.title}</span>
                  </span>
                  {type.recommended && (
                    <Star className="w-3.5 h-3.5 md:w-4 md:h-4 fill-current flex-shrink-0" />
                  )}
                </TabsTrigger>
              ))}
            </TabsList>

            {Object.entries(whiteningTypes).map(([key, type]) => (
              <TabsContent key={key} value={key} className="mt-0">
                <Card className="border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 shadow-xl rounded-2xl overflow-hidden">
                  <CardContent className="p-0">
                    <div className="grid lg:grid-cols-2 gap-0 [&>*]:min-w-0">
                      {/* Left Side - Information */}
                      <div className="p-4 sm:p-6 lg:p-12 min-w-0">
                        {/* Recommended pill — own row on mobile so it doesn't crowd the header. */}
                        {type.recommended && (
                          <div className="mb-4 flex justify-end lg:hidden">
                            <div className="bg-gold text-navy px-3 py-1 rounded-full text-xs font-semibold inline-flex items-center gap-1">
                              <Star className="w-3.5 h-3.5 fill-current" />
                              {t("whiteningTypes.recommended")}
                            </div>
                          </div>
                        )}

                        <div className="flex items-center gap-3 sm:gap-4 mb-6">
                          <div className="w-12 h-12 sm:w-16 sm:h-16 bg-gold/20 rounded-2xl flex items-center justify-center shrink-0">
                            <type.icon className="w-6 h-6 sm:w-8 sm:h-8 text-gold" />
                          </div>
                          <div className="min-w-0 flex-1">
                            <h3 className="text-lg sm:text-2xl font-bold text-navy dark:text-white leading-tight">
                              {type.title}
                            </h3>
                            <p className="text-sm sm:text-base text-slate-600 dark:text-slate-300">
                              {type.subtitle}
                            </p>
                          </div>
                          {type.recommended && (
                            <div className="ml-auto hidden lg:block">
                              <div className="bg-gold text-navy px-3 py-1 rounded-full text-sm font-semibold flex items-center gap-1">
                                <Star className="w-4 h-4 fill-current" />
                                {t("whiteningTypes.recommended")}
                              </div>
                            </div>
                          )}
                        </div>

                        <p className="text-slate-600 dark:text-slate-300 leading-relaxed mb-8">
                          {type.description}
                        </p>

                        {/* Quick Stats */}
                        <div className="grid grid-cols-3 gap-2 sm:gap-4 mb-8">
                          <div className="text-center p-2 sm:p-4 bg-slate-50 dark:bg-slate-900 rounded-xl min-w-0">
                            <Clock className="w-5 h-5 sm:w-6 sm:h-6 text-gold mx-auto mb-1 sm:mb-2" />
                            <div className="text-xs sm:text-sm font-semibold text-navy dark:text-white">
                              {t("whiteningTypes.duration")}
                            </div>
                            <div className="text-[11px] sm:text-xs text-slate-600 dark:text-slate-300 break-words">
                              {type.duration}
                            </div>
                          </div>
                          <div className="text-center p-2 sm:p-4 bg-slate-50 dark:bg-slate-900 rounded-xl min-w-0">
                            <Sparkles className="w-5 h-5 sm:w-6 sm:h-6 text-gold mx-auto mb-1 sm:mb-2" />
                            <div className="text-xs sm:text-sm font-semibold text-navy dark:text-white">
                              {t("whiteningTypes.results")}
                            </div>
                            <div className="text-[11px] sm:text-xs text-slate-600 dark:text-slate-300 break-words">
                              {type.results}
                            </div>
                          </div>
                          <div className="text-center p-2 sm:p-4 bg-slate-50 dark:bg-slate-900 rounded-xl min-w-0">
                            <Shield className="w-5 h-5 sm:w-6 sm:h-6 text-gold mx-auto mb-1 sm:mb-2" />
                            <div className="text-xs sm:text-sm font-semibold text-navy dark:text-white">
                              {t("whiteningTypes.price")}
                            </div>
                            <div className="text-[11px] sm:text-xs text-slate-600 dark:text-slate-300 break-words">
                              {type.price}
                            </div>
                          </div>
                        </div>

                        <Button
                          className="w-full bg-gold hover:bg-gold-600 text-navy font-semibold py-3 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 flex items-center justify-center gap-2"
                          onClick={() => {
                            const pricingSection =
                              document.getElementById("pricing");
                            pricingSection?.scrollIntoView({
                              behavior: "smooth",
                            });
                          }}
                        >
                          <span>{t("whiteningTypes.cta")}</span>
                          <ArrowRight className="w-5 h-5" />
                        </Button>
                      </div>

                      {/* Right Side - Advantages & Disadvantages */}
                      <div className="p-4 sm:p-6 lg:p-12 bg-gradient-to-br from-slate-50 to-white dark:from-slate-900 dark:to-slate-800 min-w-0">
                        <div className="space-y-8">
                          {/* Advantages */}
                          <div>
                            <h4 className="text-lg font-bold text-navy dark:text-white mb-4 flex items-center gap-2">
                              <CheckCircle className="w-5 h-5 text-green-500" />
                              {t("whiteningTypes.advantages")}
                            </h4>
                            <ul className="space-y-3">
                              {type.advantages.map((advantage, index) => (
                                <li
                                  key={index}
                                  className="flex items-start gap-3"
                                >
                                  <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                                  <span className="text-slate-600 dark:text-slate-300 text-sm">
                                    {advantage}
                                  </span>
                                </li>
                              ))}
                            </ul>
                          </div>

                          {/* Disadvantages */}
                          <div>
                            <h4 className="text-lg font-bold text-navy dark:text-white mb-4 flex items-center gap-2">
                              <X className="w-5 h-5 text-red-500" />
                              {t("whiteningTypes.considerations")}
                            </h4>
                            <ul className="space-y-3">
                              {type.disadvantages.map((disadvantage, index) => (
                                <li
                                  key={index}
                                  className="flex items-start gap-3"
                                >
                                  <X className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0" />
                                  <span className="text-slate-600 dark:text-slate-300 text-sm">
                                    {disadvantage}
                                  </span>
                                </li>
                              ))}
                            </ul>
                          </div>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>
            ))}
          </Tabs>
        </div>
      </div>
    </section>
  );
}
